/**
 * Interceptor HTTP: adjunta Bearer, renueva sesión en 401 y maneja 403.
 */
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { ErrorBannerService } from '../../../core/http/error-banner.service';
import {
  isSessionExpiredHttpError,
  resolveApiErrorMessage,
} from '../../../core/http/resolve-http-error-message';
import { isOidcProviderUrl } from '../auth.config';
import { AUTH_RETRY_HEADER } from '../constants/auth-session.constants';
import { AuthService } from '../services/auth.service';

/**
 * Adjunta Bearer a las APIs Colmena. Ante 401 fuerza un refresh (salvo que el
 * Bearer ya haya rotado) y un reintento; si fallan, limpia la sesión y va a login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const errorBanner = inject(ErrorBannerService);

  if (isOidcProviderUrl(req.url)) {
    return next(req);
  }

  const token = authService.getAccessToken();
  const authReq =
    token && !req.headers.has('Authorization')
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 403) {
        errorBanner.show(
          resolveApiErrorMessage(
            error,
            'No tiene permiso para realizar esta acción. Si cree que es un error, contacte al administrador.',
          ),
        );
        return throwError(() => error);
      }

      if (!(error instanceof HttpErrorResponse) || !isSessionExpiredHttpError(error)) {
        return throwError(() => error);
      }

      if (authReq.headers.has(AUTH_RETRY_HEADER)) {
        redirectToExpiredSession(authService, router);
        return throwError(() => error);
      }

      return from(refreshSessionAfterUnauthorized(authReq, authService)).pipe(
        switchMap((tokenReady) => {
          if (!tokenReady) {
            redirectToExpiredSession(authService, router);
            return throwError(() => error);
          }

          return next(cloneWithRefreshedToken(authReq, authService.getAccessToken())).pipe(
            catchError((retryError: unknown) => {
              if (
                retryError instanceof HttpErrorResponse &&
                isSessionExpiredHttpError(retryError)
              ) {
                redirectToExpiredSession(authService, router);
              }
              return throwError(() => retryError);
            }),
          );
        }),
      );
    }),
  );
};

/**
 * Tras un 401, fuerza refresh salvo que otra petición ya haya rotado el Bearer.
 */
function refreshSessionAfterUnauthorized(
  authReq: HttpRequest<unknown>,
  authService: AuthService,
): Promise<boolean> {
  const sentBearer = authReq.headers.get('Authorization');
  const currentToken = authService.getAccessToken();
  const currentBearer = currentToken ? `Bearer ${currentToken}` : null;
  if (currentBearer && sentBearer && currentBearer !== sentBearer) {
    return Promise.resolve(true);
  }
  return authService.ensureValidAccessToken({ forceRefresh: true });
}

/**
 * Clona el request con el Bearer renovado y el flag de un solo reintento.
 */
function cloneWithRefreshedToken(
  req: HttpRequest<unknown>,
  refreshedToken: string | null,
): HttpRequest<unknown> {
  const headers: Record<string, string> = {
    [AUTH_RETRY_HEADER]: '1',
  };
  if (refreshedToken) {
    headers['Authorization'] = `Bearer ${refreshedToken}`;
  }
  return req.clone({ setHeaders: headers });
}

/** Cierra sesión local y navega a login con `session-expired` si no está ya ahí. */
function redirectToExpiredSession(authService: AuthService, router: Router): void {
  authService.clearExpiredSession();
  if (!router.url.startsWith('/login')) {
    void router.navigate(['/login'], {
      queryParams: { loginError: 'session-expired' },
    });
  }
}
